import {
  getBillingAccountAlias,
  joinParts,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { Country, SCHWEIZ } from "@/constants/countries";
import {
  Address,
  BasicCustomerData,
  CreateBillingAccountRequest,
  DrAccount,
} from "@/services/model";
import { getCountryFromId } from "@/utils/countries";

export type BillingAccountFormValues = {
  alias: string;
  firstName: string;
  lastName: string;
  companyName: string;
  additionalAddress: string;
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
  country: Country;
  email: string;
  reference: string;
  note: string;
};

const getAddressValues = (address?: Address) => ({
  additionalAddress: address?.postalDeliveryNotes ?? "",
  street: address?.street ?? "",
  houseNumber: address?.houseNumber ?? "",
  zip: address?.zip ?? "",
  city: address?.city ?? "",
  country:
    address?.country?.id == null
      ? SCHWEIZ
      : (getCountryFromId(address.country.id) ?? SCHWEIZ),
});

/** A new Vertragskonto starts with the customer's master data. */
export const getCustomerFormValues = (
  customer?: BasicCustomerData,
): BillingAccountFormValues => {
  const fullName = joinParts(customer?.firstName, customer?.lastName);

  return {
    alias: customer?.companyName || fullName,
    firstName: customer?.firstName ?? "",
    lastName: customer?.lastName ?? "",
    companyName: customer?.companyName ?? "",
    ...getAddressValues(customer?.mainAddress),
    email: "",
    reference: "",
    note: "",
  };
};

export const getAccountFormValues = (
  account: DrAccount,
): BillingAccountFormValues => ({
  alias: getBillingAccountAlias(account),
  firstName: account.billToAddress?.firstName ?? "",
  lastName: account.billToAddress?.lastName ?? "",
  companyName: account.billToAddress?.companyName ?? "",
  ...getAddressValues(account.billToAddress),
  email: account.billingEmail ?? "",
  reference: account.billingReference ?? "",
  note: account.billingNotes ?? "",
});

const optional = (value: string) => value.trim() || undefined;

/** Create and update share the same body. */
export const toBillingAccountRequest = (
  values: BillingAccountFormValues,
): CreateBillingAccountRequest => ({
  billingAccountAlias: optional(values.alias),
  firstName: optional(values.firstName),
  lastName: optional(values.lastName),
  companyName: optional(values.companyName),
  billingAdditionalAddressDetail: optional(values.additionalAddress),
  street: optional(values.street),
  houseNumber: optional(values.houseNumber),
  zip: optional(values.zip),
  city: optional(values.city),
  country: { id: values.country.ID },
  billingEmail: values.email.trim(),
  billingReference: values.reference.trim(),
  billingNote: optional(values.note),
});
